import { Box, Typography, FormControlLabel, Checkbox, useTheme } from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { System, Asset} from "../../state/types";
import { State } from '../../state/reducers';
import { useSelector } from "react-redux";
import { useAsset } from "../../context/AssetContext";

const AssetSidebar: React.FC = () => {
  const theme = useTheme();
  const systems: System[] = useSelector((state: State) => state.systemState.systems)

  const {
    asset, 
    visibleTypes, 
    setVisibleTypes,
    imageCategories
  } = useAsset();

  if (!asset) return <></>

  const handleCheckboxChange = (key: string) => {
    setVisibleTypes((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const getSyndicationStep = (asset: Asset) => {
    if (!asset || !asset.syndicationSteps || !asset.syndicationCurrentStep) return ''

    if (asset.syndicationCurrentStep === "COMPLETE") return `${asset?.syndicationSteps[0]} COMPLETE`

    return asset.syndicationCurrentStep
  }

  const getCommonNameForSystem = (systemCode: string) => {
    return systems?.find((sys: System) => sys.systemCode === systemCode)?.commonName
  }

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        position: { xs: "relative", md: "sticky" },
        top: { md: "5px" },
        height: "100%",
        maxHeight: { xs: "auto", md: "calc(100vh - 120px)" },
        borderRight: { md: `5px solid ${theme.palette.divider}` },
        borderBottom: { xs: `5px solid ${theme.palette.divider}`, md: "none" },
        overflowY: "auto",
        zIndex: 2,
        p: 2
      }}
    >
      <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: "bold", mb: 2 }}>
        Asset Info
      </Typography>

      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Ran at:</strong> {new Date(+asset.created_at * 1000).toLocaleString()}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Asset ID:</strong> {asset.assetIdentifier}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client ID:</strong> {asset.clientId}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Client Name:</strong> {getCommonNameForSystem(asset.clientId || "")}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Config Name:</strong> {asset.config}
      </Typography>
      <Typography variant="body2" sx={{ mb: 1 }}>
        <strong>Processing Status:</strong> {asset.processingCurrentStep?.toUpperCase()}
      </Typography>

      {asset.processingError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.processingError}
          </Typography>
        </Box>
      )}

      <Typography variant="body2" sx={{ mb: 2 }}>
        <strong>Syndication Status:</strong> {getSyndicationStep(asset)}
      </Typography>

      {asset.syndicationError && (
        <Box
          sx={{
            backgroundColor: theme.palette.error.main,
            color: "white",
            borderRadius: 1,
            py: 1,
            px: 2,
            mb: 1,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <WarningAmberIcon sx={{ fontSize: "1.2rem" }} />
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            {asset.syndicationError}
          </Typography>
        </Box>
      )}

      <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
        Image Categories
      </Typography>

      {imageCategories.map(({ key, label, images }) => (
        <FormControlLabel
          key={key}
          control={
            <Checkbox
              checked={visibleTypes[key]}
              onChange={() => handleCheckboxChange(key)}
              disabled={!images.length}
              sx={{
                "&.Mui-checked": {
                  color: "darkred",
                },
              }}
            />
          }
          label={`${label} (${images.length})`}
          sx={{
            opacity: images.length ? 1 : 0.5,
          }}
        />
      ))}
    </Box>
  );
};

export default AssetSidebar;
